import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getBreadcrumbs, getPageTitle } from '@/config/routes';
import './Header.css';

/**
 * Header component props
 */
export interface HeaderProps {
  /**
   * Optional page title override
   */
  title?: string;

  /**
   * Optional additional CSS class
   */
  className?: string;
}

/**
 * Header component that displays the page title, breadcrumbs, and user menu.
 * Titles come from the route config (config/routes.ts).
 *
 * @example
 * ```tsx
 * <Header />
 *
 * // With custom title
 * <Header title="Custom Page" />
 * ```
 */
export function Header({ title, className = '' }: HeaderProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  /**
   * Handle user logout
   */
  const handleLogout = async () => {
    setIsUserMenuOpen(false);
    try {
      await logout();
      navigate('/login', { replace: true });
    } catch {
      // Logout failed but we should still redirect
      navigate('/login', { replace: true });
    }
  };

  /**
   * Toggle user menu
   */
  const toggleUserMenu = () => {
    setIsUserMenuOpen((prev) => !prev);
  };

  /**
   * Close menu when clicking outside
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  /**
   * Close menu on escape key
   */
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isUserMenuOpen]);

  const breadcrumbs = getBreadcrumbs(location.pathname);
  const pageTitle = title ?? getPageTitle(location.pathname);
  const headerClasses = ['layout-header', className].filter(Boolean).join(' ');

  return (
    <header className={headerClasses}>
      <div className="header-left">
        {/* Breadcrumbs */}
        {breadcrumbs.length > 1 && (
          <nav className="header-breadcrumbs" aria-label="Breadcrumb">
            <ol className="breadcrumb-list">
              {breadcrumbs.map((crumb, index) => (
                <li key={index} className="breadcrumb-item">
                  {crumb.path ? (
                    <a
                      href={crumb.path}
                      className="breadcrumb-link"
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(crumb.path!);
                      }}
                    >
                      {crumb.label}
                    </a>
                  ) : (
                    <span className="breadcrumb-current" aria-current="page">
                      {crumb.label}
                    </span>
                  )}
                  {index < breadcrumbs.length - 1 && (
                    <span className="breadcrumb-separator" aria-hidden="true">
                      /
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        )}

        {/* Page Title */}
        <h1 className="header-title">{pageTitle}</h1>
      </div>

      <div className="header-right">
        {/* User Menu */}
        <div className="user-menu-container" ref={menuRef}>
          <button
            type="button"
            className="user-menu-trigger"
            onClick={toggleUserMenu}
            aria-expanded={isUserMenuOpen}
            aria-haspopup="true"
          >
            <span className="user-menu-email" title={user?.email}>
              {user?.email}
            </span>
            {user?.role && (
              <span className={`role-badge role-badge-${user.role}`}>
                {user.role}
              </span>
            )}
            <span className="user-menu-arrow" aria-hidden="true">
              {isUserMenuOpen ? '▲' : '▼'}
            </span>
          </button>

          {isUserMenuOpen && (
            <div className="user-menu-dropdown" role="menu">
              <div className="user-menu-header">
                <span className="user-menu-name">{user?.email}</span>
                <span className="user-menu-role">
                  Role: <strong>{user?.role}</strong>
                </span>
              </div>
              <div className="user-menu-divider" />
              <button
                type="button"
                className="user-menu-item user-menu-logout"
                onClick={handleLogout}
                role="menuitem"
              >
                <span className="user-menu-item-icon" aria-hidden="true">🚪</span>
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
